// A22 — Lokalizatsiya. Module-local data layer for the 4 screens that need a
// BACKEND but don't have one yet (A22.07/08/09/12). Mirrors the developers
// pattern (`developers/devApi.ts`): `safeList` fail-soft to empty + module-local
// RU strings (LOC_STR) — does NOT touch global `i18n/*` (that catalog is HOT and
// is the source for the client-side A22.01/03/05/10/14 screens).
//
// The live Go core exposes 7 localization routes (`/admin/localization/{langs,
// strings,missing,stats,export}` + PUT/DELETE strings). NONE of them cover
// versioning, community queue, translator rights or context screenshots, so
// every fetcher below reads from a greenfield `/admin/localization/*` path and
// fails soft to `[]` until the backend ships (exactly like developers/ads/seo).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ── A22.07 — Versiyalash va tarqatish ───────────────────────────── */

/** A22.07 — a published localization pack version + its rollout state. */
export interface PackVersionRow {
  id: number
  version: string
  lang: string
  status: 'draft' | 'staged' | 'rolling' | 'live' | 'rolled_back'
  rollout_pct: number
  keys: number
  author: string
  published_at: string | null
}

export function fetchPackVersions(params: FetchParams): Promise<FetchResult<PackVersionRow>> {
  return safeList<PackVersionRow>('/admin/localization/versions', 'versions', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.version} ${r.lang} ${r.author}`),
  )
}

/** POST /admin/localization/versions/:id/rollout {pct} — promote rollout (A22.07). */
export function rolloutVersion(id: number, pct: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/localization/versions/${id}/rollout`, { pct })
}

/** POST /admin/localization/versions/:id/rollback — revert to previous pack (A22.07). */
export function rollbackVersion(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/localization/versions/${id}/rollback`, {})
}

/* ── A22.08 — Ommaviy tarjima (jamoa) ────────────────────────────── */

/** A22.08 — a community-submitted translation awaiting moderation. */
export interface CommunitySubmissionRow {
  id: number
  key: string
  lang: string
  suggestion: string
  current: string
  author: string
  votes: number
  status: 'pending' | 'approved' | 'rejected'
  submitted_at: string
}

export function fetchCommunity(params: FetchParams): Promise<FetchResult<CommunitySubmissionRow>> {
  return safeList<CommunitySubmissionRow>(
    '/admin/localization/community',
    'submissions',
    params.signal,
  ).then((rows) => toResult(rows, params, (r) => `${r.key} ${r.lang} ${r.author} ${r.suggestion}`))
}

/** POST /admin/localization/community/:id/:action — approve|reject (A22.08). */
export function resolveCommunity(
  id: number,
  action: 'approve' | 'reject',
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/localization/community/${id}/${action}`, {})
}

/* ── A22.09 — Tarjimon huquqlari ─────────────────────────────────── */

/** A22.09 — a translator and the rights granted to them. */
export interface TranslatorRow {
  id: number
  name: string
  languages: string[]
  role: 'translator' | 'reviewer' | 'lead'
  can_publish: boolean
  strings_translated: number
  status: 'active' | 'suspended'
  added_at: string
}

export function fetchTranslators(params: FetchParams): Promise<FetchResult<TranslatorRow>> {
  return safeList<TranslatorRow>('/admin/localization/translators', 'translators', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.name} ${r.role} ${r.languages.join(' ')}`),
  )
}

/** POST /admin/localization/translators/:id/status {status} — active|suspended (A22.09). */
export function setTranslatorStatus(id: number, status: string): Promise<{ status: string }> {
  return api.post<{ status: string }>(`/admin/localization/translators/${id}/status`, { status })
}

/* ── A22.12 — Kontekst skrinshotlari ─────────────────────────────── */

/** A22.12 — a context screenshot attached to one or more i18n keys. */
export interface ScreenshotRow {
  id: number
  title: string
  screen: string
  key: string
  linked_keys: number
  uploaded_by: string
  created_at: string
}

export function fetchScreenshots(params: FetchParams): Promise<FetchResult<ScreenshotRow>> {
  return safeList<ScreenshotRow>('/admin/localization/screenshots', 'screenshots', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.title} ${r.screen} ${r.key} ${r.uploaded_by}`),
  )
}

/** POST /admin/localization/screenshots/:id/delete — detach screenshot (A22.12). */
export function deleteScreenshot(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/localization/screenshots/${id}/delete`, {})
}

/* ── A22.13 — Glossariy (admin-boshqariladigan) ──────────────────── */

/** A22.13 — a curated domain-term translation (admin-managed glossary). */
export interface GlossaryTermRow {
  id: number
  en: string
  ru: string
  uz: string
  note: string
}

/** GET /admin/localization/glossary → {terms:[...]} (JONLI, admin-managed). */
export function fetchGlossary(signal: AbortSignal): Promise<GlossaryTermRow[]> {
  return safeList<GlossaryTermRow>('/admin/localization/glossary', 'terms', signal)
}

/** POST /admin/localization/glossary — add a term (A22.13). */
export function createGlossary(term: {
  en: string
  ru: string
  uz: string
  note: string
}): Promise<{ id: number }> {
  return api.post<{ id: number }>('/admin/localization/glossary', term)
}

/** DELETE /admin/localization/glossary/:id — remove a term (A22.13). */
export function deleteGlossary(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/localization/glossary/${id}`)
}

/* ── A22.06 — Pluralizatsiya qoidalari (JONLI, admin-managed) ──────
   CLDR ko'plik-kategoriyalari reyestri (til bo'yicha). Avval FE'da statik
   massiv edi; endi GET/POST/PUT/DELETE /admin/localization/plurals (uz/ru/en
   standart CLDR bilan urug'langan). */
export interface PluralCat {
  category: string
  example: string
  rule: string
}
export interface PluralRow {
  id: number
  lang_code: string
  lang_name: string
  categories: PluralCat[]
  created_at: string
  updated_at: string
}
/** GET /admin/localization/plurals → {plurals:[...]} (JONLI). */
export function fetchPlurals(signal: AbortSignal): Promise<PluralRow[]> {
  return safeList<PluralRow>('/admin/localization/plurals', 'plurals', signal)
}
/** POST /admin/localization/plurals — add a language's plural rules. */
export function createPlural(body: {
  lang_code: string
  lang_name: string
  categories: PluralCat[]
}): Promise<{ id: number }> {
  return api.post<{ id: number }>('/admin/localization/plurals', body)
}
/** PUT /admin/localization/plurals/:id — replace name + categories. */
export function updatePlural(
  id: number,
  body: { lang_name: string; categories: PluralCat[] },
): Promise<{ ok: boolean }> {
  return api.put<{ ok: boolean }>(`/admin/localization/plurals/${id}`, body)
}
/** DELETE /admin/localization/plurals/:id — remove a language. */
export function deletePlural(id: number): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`/admin/localization/plurals/${id}`)
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, developers/REGISTER.md uslubi). */
export const LOC_STR = {
  back: 'Локализация',
  failed: 'Не удалось выполнить',
  // A22.07 versioning + rollout
  versionsTitle: 'Версионирование и раскатка',
  version: 'Версия',
  language: 'Язык',
  status: 'Статус',
  rolloutPct: 'Раскатка, %',
  keys: 'Ключей',
  author: 'Автор',
  publishedAt: 'Опубликовано',
  statusDraft: 'Черновик',
  statusStaged: 'Подготовлено',
  statusRolling: 'Раскатывается',
  statusLive: 'В проде',
  statusRolledBack: 'Откат',
  rollout: 'Раскатать шире',
  rollback: 'Откатить',
  rolledOut: 'Раскатка обновлена',
  rolledBack: 'Версия откачена',
  // A22.08 community queue
  communityTitle: 'Народный перевод (сообщество)',
  key: 'Ключ',
  suggestion: 'Предложение',
  current: 'Текущий',
  votes: 'Голосов',
  submittedAt: 'Отправлено',
  statusPending: 'На модерации',
  statusApproved: 'Одобрено',
  statusRejected: 'Отклонено',
  approve: 'Одобрить',
  reject: 'Отклонить',
  approved: 'Перевод одобрен',
  rejected: 'Перевод отклонён',
  // A22.09 translator rights
  translatorsTitle: 'Права переводчиков',
  name: 'Имя',
  languages: 'Языки',
  role: 'Роль',
  canPublish: 'Публикация',
  stringsTranslated: 'Переведено строк',
  addedAt: 'Добавлен',
  roleTranslator: 'Переводчик',
  roleReviewer: 'Ревьюер',
  roleLead: 'Ведущий',
  statusActive: 'Активен',
  statusSuspended: 'Приостановлен',
  suspend: 'Приостановить',
  activate: 'Активировать',
  statusChanged: 'Статус обновлён',
  // A22.12 context screenshots
  screenshotsTitle: 'Контекстные скриншоты',
  title: 'Название',
  screen: 'Экран',
  linkedKeys: 'Привязано ключей',
  uploadedBy: 'Загрузил',
  createdAt: 'Загружен',
  remove: 'Удалить',
  removed: 'Скриншот удалён',
  // A22.13 glossary (admin-managed)
  addTerm: 'Добавить термин',
  notePh: 'Заметка',
  termAdded: 'Термин добавлен',
  termRemoved: 'Термин удалён',
  // A22.06 pluralization rules (admin-managed)
  pluralCode: 'Код языка',
  pluralName: 'Название',
  pluralCatsLabel: 'Категории',
  pluralCatsHint: 'По строке на категорию: category | пример | правило',
  pluralAdd: 'Добавить язык',
  pluralSave: 'Сохранить',
  pluralCancel: 'Отмена',
  pluralEdit: 'Изменить',
  pluralAdded: 'Язык добавлен',
  pluralUpdated: 'Правила обновлены',
  pluralRemoved: 'Язык удалён',
  pluralNone: 'Правил пока нет',
  pluralCats: 'категорий',
} as const
